"use client";

import { useEffect, useId, useState } from "react";

export interface OptionalNumberOptions {
  label: string;
  min: number;
  max: number;
}

interface OptionalChoiceOptions {
  label: string;
  options: { label: string; value: number }[];
}

export type OptionalNumberGroupOptions = Record<
  string,
  OptionalNumberOptions | OptionalChoiceOptions
>;
type OptionalNumberGroupValue = Record<string, number | undefined> | undefined;

const inputStyle = {
  border: "1px solid #c4c8cf",
  borderRadius: 4,
  padding: "8px 10px",
  font: "inherit",
  minWidth: 0,
  width: "100%",
  boxSizing: "border-box" as const,
};
const resetStyle = { font: "inherit", fontSize: 12, cursor: "pointer" };

/** Empty is an unset override, while a valid zero remains an explicit value. */
export function OptionalNumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  readOnly,
}: OptionalNumberOptions & {
  id: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  readOnly?: boolean;
}) {
  const instanceId = useId();
  const domId = `${id}-${instanceId}`;
  const [draft, setDraft] = useState(value === undefined ? "" : String(value));
  useEffect(() => setDraft(value === undefined ? "" : String(value)), [value]);
  const number = Number(draft);
  const invalid = draft !== "" && (!Number.isInteger(number) || number < min || number > max);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
      >
        <label htmlFor={domId}>{label}</label>
        <button
          type="button"
          aria-label={`Reset ${label}`}
          disabled={readOnly || (value === undefined && draft === "")}
          style={resetStyle}
          onClick={() => {
            setDraft("");
            onChange(undefined);
          }}
        >
          Reset
        </button>
      </div>
      <input
        id={domId}
        type="number"
        min={min}
        max={max}
        step={1}
        placeholder="Default"
        value={draft}
        disabled={readOnly}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `${domId}-error` : undefined}
        style={inputStyle}
        onChange={(event) => {
          const next = event.currentTarget.value;
          setDraft(next);
          if (next === "") {
            onChange(undefined);
            return;
          }
          const parsed = Number(next);
          if (Number.isInteger(parsed) && parsed >= min && parsed <= max) onChange(parsed);
        }}
        onBlur={() => {
          if (invalid) setDraft(value === undefined ? "" : String(value));
        }}
      />
      {invalid && (
        <small id={`${domId}-error`} role="status">
          Enter a whole number from {min} to {max}.
        </small>
      )}
    </div>
  );
}

/** Resetting the final member also removes its group, restoring normal layout inheritance. */
export function OptionalNumberGroupField({
  id,
  label,
  fields,
  value,
  onChange,
  readOnly,
}: {
  id: string;
  label: string;
  fields: OptionalNumberGroupOptions;
  value: OptionalNumberGroupValue;
  onChange: (value: OptionalNumberGroupValue) => void;
  readOnly?: boolean;
}) {
  const instanceId = useId();
  const [resetVersion, setResetVersion] = useState(0);
  const set = (key: string, next: number | undefined) => {
    const updated = { ...value, [key]: next };
    const defined = Object.fromEntries(
      Object.entries(updated).filter(([, item]) => item !== undefined)
    );
    onChange(Object.keys(defined).length ? defined : undefined);
  };
  return (
    <fieldset style={{ margin: 0, padding: 12, border: "1px solid #d6d9df", borderRadius: 4 }}>
      <legend>{label}</legend>
      <div style={{ display: "grid", gap: 12 }}>
        <button
          type="button"
          aria-label={`Reset ${label}`}
          disabled={readOnly || value === undefined}
          style={{ ...resetStyle, justifySelf: "end" }}
          onClick={() => {
            setResetVersion((version) => version + 1);
            onChange(undefined);
          }}
        >
          Reset all
        </button>
        {Object.entries(fields).map(([key, options]) => {
          if (!("options" in options))
            return (
              <OptionalNumberField
                key={`${key}-${resetVersion}`}
                id={`${id}-${key}`}
                {...options}
                value={value?.[key]}
                onChange={(next) => set(key, next)}
                readOnly={readOnly}
              />
            );
          const domId = `${id}-${key}-${instanceId}`;
          return (
            <div key={key} style={{ display: "grid", gap: 6 }}>
              <label htmlFor={domId}>{options.label}</label>
              <select
                id={domId}
                value={value?.[key] ?? ""}
                disabled={readOnly}
                style={inputStyle}
                onChange={(event) =>
                  set(
                    key,
                    event.currentTarget.value === "" ? undefined : Number(event.currentTarget.value)
                  )
                }
              >
                <option value="">Default</option>
                {options.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
